import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product } from '../types';
import { apiRequest } from '../utils/api';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';

interface ProductContextType {
  selectedProduct: Product | null;
  products: Product[];
  setSelectedProduct: (product: Product | null) => void;
  loading: boolean;
  refreshProducts: () => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};

interface ProductProviderProps {
  children: ReactNode;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const [selectedProduct, setSelectedProductState] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const fetchProducts = async () => {
    try {
      const data = await apiRequest<Product[]>('/api/products', {}, { useAuth: true });
      setProducts(data || []);
      return data || [];
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  };

  const refreshProducts = async () => {
    const fetchedProducts = await fetchProducts();
    // If current product is no longer in list, clear it
    if (selectedProduct && currentWorkspace) {
      if (!fetchedProducts.find(p => p.id === selectedProduct.id)) {
        setSelectedProductState(null);
        const storageKey = `selectedProductId_${currentWorkspace.id}`;
        localStorage.removeItem(storageKey);
      }
    }
  };

  // Load products and selected product
  useEffect(() => {
    const loadProducts = async () => {
      if (!isAuthenticated || !currentWorkspace) {
        setLoading(false);
        return;
      }

      try {
        const fetchedProducts = await fetchProducts();
        
        // Load selected product from localStorage (workspace-specific, persisted across sessions)
        const storageKey = `selectedProductId_${currentWorkspace.id}`;
        const storedProductId = localStorage.getItem(storageKey);
        if (storedProductId) {
          const product = fetchedProducts.find(p => p.id === storedProductId);
          if (product) {
            setSelectedProductState(product);
            // Ensure it's saved (in case it wasn't before)
            localStorage.setItem(storageKey, product.id);
          } else {
            // Product no longer exists, clear it and select first available
            localStorage.removeItem(storageKey);
            if (fetchedProducts.length > 0) {
              setSelectedProductState(fetchedProducts[0]);
              localStorage.setItem(storageKey, fetchedProducts[0].id);
            }
          }
        } else if (fetchedProducts.length > 0) {
          // Auto-select first product if none selected
          setSelectedProductState(fetchedProducts[0]);
          localStorage.setItem(storageKey, fetchedProducts[0].id);
        }
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [isAuthenticated, currentWorkspace]);

  // Listen for storage changes
  useEffect(() => {
    if (!currentWorkspace) return;
    
    const storageKey = `selectedProductId_${currentWorkspace.id}`;
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        const product = products.find(p => p.id === e.newValue);
        if (product) {
          setSelectedProductState(product);
        }
      } else if (e.key === storageKey && !e.newValue) {
        setSelectedProductState(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [products, currentWorkspace]);

  const setSelectedProduct = (product: Product | null) => {
    setSelectedProductState(product);
    if (currentWorkspace) {
      const storageKey = `selectedProductId_${currentWorkspace.id}`;
      if (product) {
        // Persist to localStorage so it's remembered across sessions (workspace-specific)
        localStorage.setItem(storageKey, product.id);
      } else {
        localStorage.removeItem(storageKey);
      }
    }
  };

  const value: ProductContextType = {
    selectedProduct,
    products,
    setSelectedProduct,
    loading,
    refreshProducts
  };

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

